// Onglet « Avancement » de la page du chantier (incrément 4) : trois mesures TOUJOURS séparées (conception § 3.4) — étapes faites,
// heures prévues des étapes faites, heures passées déclarées. Aucun pourcentage, aucune date de fin devinée, aucun montant.
// Puis le détail par poste et les jours travaillés (chacun ouvre sa page du jour).
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, CheckCircle2, CircleDashed, Clock, History, ListChecks } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { dateHeure, nombre, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Chargement, EtatErreur, EtatVide, LienBouton, Panneau } from '../../../components/ui.tsx';
import { BadgesRevision, heures, quantite } from './commun.tsx';
import { jourCourt } from './jours.ts';
import { jourHeure } from './Journal.tsx';
import type { Avancement, HistoriqueValeur, MesuresAvancement } from './types.ts';

/** Les trois mesures, côte à côte, jamais fondues en un seul chiffre. */
export function TroisMesures({ m, tempsDesactive = false }: { m: MesuresAvancement; tempsDesactive?: boolean }) {
  const e = m.etapes;
  return (
    <dl className="ch-chiffres av-mesures">
      <div>
        <dt><ListChecks aria-hidden /> Étapes</dt>
        <dd className="chiffre">{e.faites} faite{e.faites > 1 ? 's' : ''} sur {e.total}</dd>
        <span className="petit secondaire">{e.en_cours ? `${pluriel(e.en_cours, 'en cours', 'en cours')}` : 'Aucune en cours'}</span>
      </div>
      <div>
        <dt><CheckCircle2 aria-hidden /> Heures prévues des étapes faites</dt>
        <dd className="chiffre">{heures(m.heures_prevues.faites)} sur {heures(m.heures_prevues.prevues)}</dd>
        <span className="petit secondaire">Reste {heures(m.heures_prevues.reste)} prévues</span>
      </div>
      <div>
        <dt><Clock aria-hidden /> Heures passées</dt>
        <dd className="chiffre">{m.heures_passees.total !== null ? heures(m.heures_passees.total) : 'Aucune déclarée'}</dd>
        <span className="petit secondaire">{m.heures_passees.total !== null ? `${pluriel(m.heures_passees.declarations, 'déclaration')} des techniciens`
          : tempsDesactive ? 'Déclaration désactivée par l’entreprise' : 'Déclarées en fin de journée'}</span>
      </div>
    </dl>
  );
}

function AvisMesures({ m }: { m: MesuresAvancement }) {
  const e = m.etapes;
  const avis = [
    e.sans_temps ? `${pluriel(e.sans_temps, 'étape')} sans temps prévu${e.faites_sans_temps ? ` (dont ${e.faites_sans_temps} faite${e.faites_sans_temps > 1 ? 's' : ''})` : ''} : hors des heures.` : null,
    e.quantites_depassees ? `${pluriel(e.quantites_depassees, 'étape')} : quantité déclarée supérieure au prévu — à vérifier.` : null,
    e.faites_retirees ? `${pluriel(e.faites_retirees, 'étape faite puis retirée', 'étapes faites puis retirées')} : à arbitrer (hors des totaux).` : null,
    e.doubles_coches ? `${pluriel(e.doubles_coches, 'étape cochée', 'étapes cochées')} par plusieurs personnes : toutes les coches sont conservées.` : null,
  ].filter(Boolean) as string[];
  if (!avis.length) return null;
  return <ul className="petit secondaire av-avis">{avis.map((x) => <li key={x}>{x}</li>)}</ul>;
}

/** Valeur d'historique lisible : nombres en français (« 6 », « 1,5 ») ; le reste tel quel. */
const valeur = (champ: string, x: string | null) => (x === null ? '—' : ['qty_planned', 'hours_per_unit', 'hours_planned'].includes(champ) && /^-?\d+(\.\d+)?$/.test(x)
  ? `${nombre(x, 4)}${champ === 'qty_planned' ? '' : ' h'}` : x);

/** Historique des valeurs prévues d'une étape (reports de révision, corrections après le lancement), le plus ancien d'abord. */
function HistoriqueEtape({ h }: { h: HistoriqueValeur[] }) {
  if (!h.length) return null;
  return (
    <details className="av-historique">
      <summary><History aria-hidden /> Historique des valeurs prévues ({h.length})</summary>
      <ol className="petit">
        {h.map((x, i) => (
          <li key={i}>
            <span className="chiffre">{dateHeure(x.le)}</span> — <strong>{x.libelle}</strong>
            {x.champ === 'retrait' || x.champ === 'retrait_devis' || x.champ === 'ajout' ? ` : ${x.apres ?? ''}` : ` : ${valeur(x.champ, x.avant)} → ${valeur(x.champ, x.apres)}`}
            {x.rev_no ? ` — révision ${x.rev_no}` : ''} — {x.par}
            {x.motif && !(x.rev_no && x.motif.startsWith(`Révision ${x.rev_no} du devis`)) && <span className="secondaire expert-seulement"> — {x.motif}</span>}
          </li>
        ))}
      </ol>
    </details>
  );
}

function EtatEtape({ statut, retiree }: { statut: string; retiree: boolean }) {
  if (retiree) return <Badge ton="neutre" icone={false}>Retirée</Badge>;
  if (statut === 'fait') return <Badge ton="succes" icone={<CheckCircle2 size={14} aria-hidden />}>Faite</Badge>;
  if (statut === 'en_cours') return <Badge ton="info" icone={<CircleDashed size={14} aria-hidden />}>En cours</Badge>;
  return <Badge ton="neutre" icone={false}>À faire</Badge>;
}

export function PanneauAvancement({ chantierId }: { chantierId: string }) {
  const q = useQuery({ queryKey: ['chantier-avancement', chantierId], queryFn: () => api.get<Avancement>(`/api/chantiers/${chantierId}/avancement`) });
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  const lance = v.chantier.status !== 'preparation';
  return (
    <div className="pile-l">
      {!lance && <Alerte ton="info">Le chantier est en préparation : l’avancement commence au lancement, avec les premières coches.</Alerte>}
      <Panneau titre="Où en est le chantier">
        <div className="pile">
          <TroisMesures m={v.mesures} tempsDesactive={v.reglages?.temps === false} />
          <AvisMesures m={v.mesures} />
          {!!v.a_arbitrer && <Alerte ton="ambre" titre={`${pluriel(v.a_arbitrer, 'étape faite', 'étapes faites')} à arbitrer`}>
            Leur ligne a été retirée du devis (ou l’étape retirée du chantier) alors que le travail était fait, même en partie : travaux supplémentaires, avoir… à décider avec le client. Le détail est dans chaque poste.
          </Alerte>}
          {Number(v.temps_non_reparti) > 0 && <p className="petit discret" style={{ margin: 0 }}>Heures passées déclarées pour le chantier (sans poste précis) : {heures(v.temps_non_reparti)}.</p>}
          {v.signalements && (v.signalements.hors_poste > 0 || v.signalements.heure_incoherente > 0) && (
            <Alerte ton="ambre" titre="À regarder (responsable du chantier)">
              {[v.signalements.hors_poste ? `${pluriel(v.signalements.hors_poste, 'coche')} hors du poste du jour` : null,
                v.signalements.heure_incoherente ? `${pluriel(v.signalements.heure_incoherente, 'coche')} à l’heure du téléphone incohérente (heure du serveur retenue)` : null]
                .filter(Boolean).join(' ; ')}. Le détail est sur la page du jour.
            </Alerte>
          )}
        </div>
      </Panneau>

      <Panneau serre titre="Par poste">
        <ol className="ch-postes-lecture">
          {v.postes.map((p) => (
            <li key={p.id} className={p.retire ? 'ch-retire' : undefined}>
              <details open={v.postes.length <= 3}>
                <summary>
                  <span className="ch-poste-titre">{p.label}</span>
                  <span className="petit discret chiffre">{p.mesures.etapes.faites}/{p.mesures.etapes.total} étapes — {heures(p.mesures.heures_prevues.faites)} sur {heures(p.mesures.heures_prevues.prevues)}
                    {p.mesures.heures_passees.total !== null ? ` — ${heures(p.mesures.heures_passees.total)} passées` : ''}</span>
                  {p.place === 'bureau' && <Badge ton="info" icone={false}>Au bureau</Badge>}
                  {p.retire && <Badge ton="neutre">Retiré</Badge>}
                </summary>
                <ol className="ch-etapes-lecture av-etapes">
                  {p.etapes.map((e) => (
                    <li key={e.id} className={e.retiree ? 'ch-retire' : undefined}>
                      <span className="av-etape-texte">
                        <span className="ch-etape-label">{e.label}</span>
                        <span className="petit discret chiffre">{quantite(e.qty_planned, e.unit)}{e.hours_planned !== null ? ` — ${heures(e.hours_planned)}` : ' — sans temps prévu'}
                          {e.statut === 'en_cours' ? ` — fait : ${quantite(e.qty_done, e.unit)}` : ''}</span>
                        {e.faite_le && <span className="petit secondaire">Faite {jourHeure(e.faite_le)}{e.par.length ? ` par ${e.par.join(', ')}` : ''}{e.coches > 1 ? ` (${e.coches} coches)` : ''}</span>}
                        {e.quantite_depassee && <span className="petit jl-a-verifier">Quantité déclarée supérieure au prévu — à vérifier</span>}
                        {(e.ajoutee_par_revision || e.retiree_du_devis) && <span className="rangee" style={{ gap: 6 }}>
                          <BadgesRevision ajoutee={e.ajoutee_par_revision} retireeDuDevis={e.retiree_du_devis} faite={Number(e.qty_done) > 0} /></span>}
                        <HistoriqueEtape h={e.historique ?? []} />
                      </span>
                      <EtatEtape statut={e.statut} retiree={e.retiree} />
                    </li>
                  ))}
                </ol>
              </details>
            </li>
          ))}
        </ol>
      </Panneau>

      <Panneau titre="Jours travaillés">
        {v.jours.length === 0 ? <EtatVide titre="Aucun jour travaillé" texte="Les jours apparaissent avec les affectations, les coches, les consignes et le temps déclaré." /> : (
          <ul className="av-jours">
            {v.jours.map((j) => (
              <li key={j.jour}>
                <Link to={`/interventions/chantiers/${chantierId}/jour/${j.jour}`} className="av-jour">
                  <span className="av-jour-nom"><CalendarDays aria-hidden />{jourCourt(j.jour)}{j.jour === v.aujourdhui ? ' (aujourd’hui)' : ''}</span>
                  <span className="petit secondaire">{[j.coches ? pluriel(j.coches, 'coche') : null, j.annulations ? pluriel(j.annulations, 'annulation') : null,
                    j.consignes ? pluriel(j.consignes, 'consigne') : null, j.temps ? pluriel(j.temps, 'temps déclaré', 'temps déclarés') : null,
                    j.terrain ? pluriel(j.terrain, 'saisie de terrain', 'saisies de terrain') : null].filter(Boolean).join(' · ') || 'Affectations seulement'}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panneau>

      {lance && (
        <div className="barre-actions barre-collante">
          <span className="petit chiffre barre-collante-resume">{v.mesures.etapes.faites}/{v.mesures.etapes.total} étapes · {heures(v.mesures.heures_prevues.faites)} faites sur {heures(v.mesures.heures_prevues.prevues)}</span>
          <LienBouton variante="principal" icone={<CalendarDays aria-hidden />} to={`/interventions/chantiers/${chantierId}/jour/${v.aujourdhui}`}>Page du jour</LienBouton>
        </div>
      )}
    </div>
  );
}
